import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { api, tokenStore } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import './LoginPage.css';

/**
 * ChangePassword — shown when force_password_change === 1 after login.
 * User must set a new password before accessing the rest of the app.
 */
export function ChangePassword() {
    const navigate   = useNavigate();
    const { logout } = useAuth();

    const [newPassword,     setNewPassword]     = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error,           setError]           = useState(null);
    const [submitting,      setSubmitting]      = useState(false);

    const validate = () => {
        if (newPassword.length < 8) return 'Password must be at least 8 characters.';
        if (newPassword !== confirmPassword) return 'Passwords do not match.';
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validate();
        if (validationError) { setError(validationError); return; }

        setError(null);
        setSubmitting(true);
        try {
            const result = await api.post('/auth/change-password', { newPassword, confirmPassword });
            // Store the new JWT returned by the server
            if (result.token) tokenStore.set(result.token);
            navigate('/pipeline', { replace: true });
        } catch (err) {
            setError(err.message || 'Failed to change password. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="login">
            {/* LEFT: brand panel (reuse login styling) */}
            <section className="login-brand">
                <div className="login-brand-inner">
                    <div className="login-mark">
                        <img src="/behold-logo-white.png" alt="" className="login-mark-glyph" />
                        <span className="login-mark-text display">Behold</span>
                    </div>
                    <div className="login-quote">
                        <h1 className="display login-headline">Set your<br/><span className="accent">new password.</span></h1>
                        <p className="login-tagline">
                            Your administrator has reset your password. Please choose a new one to continue.
                        </p>
                    </div>
                    <div className="login-meta">
                        <span className="mono">Behold International</span>
                        <span className="mono">v0.1 · MVP</span>
                    </div>
                </div>
            </section>

            {/* RIGHT: form */}
            <section className="login-form-panel">
                <form className="login-form" onSubmit={handleSubmit}>
                    <div className="login-form-header">
                        <h2 className="display">Choose a new password</h2>
                        <p>Must be at least 8 characters.</p>
                    </div>

                    <label className="field">
                        <span className="field-label">New password</span>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            autoComplete="new-password"
                            required
                            autoFocus
                            placeholder="••••••••"
                            minLength={8}
                        />
                    </label>

                    <label className="field">
                        <span className="field-label">Confirm new password</span>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            autoComplete="new-password"
                            required
                            placeholder="••••••••"
                        />
                    </label>

                    {error && <div className="field-error">{error}</div>}

                    <button type="submit" className="btn-primary" disabled={submitting}>
                        {submitting ? <span className="spinner" /> : (
                            <>
                                Set password
                                <ArrowRight size={16} strokeWidth={2} />
                            </>
                        )}
                    </button>

                    <p className="login-help">
                        <button type="button" className="link-btn" onClick={logout}>
                            Sign out instead
                        </button>
                    </p>
                </form>
            </section>
        </div>
    );
}
